/* oxlint-disable typescript/no-unnecessary-condition, typescript/no-unsafe-argument, typescript/no-unsafe-assignment */
// @ts-nocheck
import { assert } from '@ember/debug';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { identify, initialize } from 'ember-launch-darkly';
import { getCurrentContext } from 'ember-launch-darkly/-sdk/context';
import window from 'ember-window-mock';

import ENV from 'register-partners-js/config/environment';

const SHARED_USER_KEY = 'unknown-web-user';

/*
  launchdarkly has a set of built-in user attributes that we should never overwrite as custom data
  since values like country or name could exist on multiple of our models we highly recommend not passing them
  if we need a a specific model's property, like `user.email`
  we recommend prefixing the key with the model name: `user_email`
  https://docs.launchdarkly.com/home/users/built-in-attributes#using-built-in-user-attributes
  https://docs.launchdarkly.com/home/users/custom-attributes
*/
const LAUNCHDARKLY_BUILT_INS = [
  'key',
  'secondary',
  'ip',
  'email',
  'name',
  'avatar',
  'firstName',
  'lastName',
  'country',
  'anonymous',
  'os',
  'device',
];

function hasNestedObjects(object) {
  let keys = Object.keys(object);
  // oxlint-disable-next-line typescript/no-unsafe-member-access
  return keys.some(key => typeof object[key] === 'object');
}

function hasBuiltInAttributes(object) {
  let keys = Object.keys(object);
  return keys.some(key => LAUNCHDARKLY_BUILT_INS.includes(key));
}

export default class LaunchdarklyService extends Service {
  @service sentry;

  @tracked isInitialized = false;

  _initialize(clientSideId, user, rest) {
    return initialize(clientSideId, user, rest);
  }

  async _identify(user) {
    return await identify(user);
  }

  async setup(key = SHARED_USER_KEY) {
    let { clientSideId, ...rest } = ENV.launchDarkly;
    let { custom, privateAttributeNames } = this._initialCustomData;
    let user = { key, custom, privateAttributeNames };
    try {
      await this._initialize(clientSideId, user, rest);
      this.isInitialized = true;
    } catch (error) {
      if (
        error?.name !== 'LaunchDarklyFlagFetchError' ||
        error?.message !== 'network error (Error)'
      ) {
        // oxlint-disable-next-line typescript/no-unsafe-member-access
        this.sentry.captureException(error);
      }
    }
  }

  async identify({ userId, customData } = {}) {
    assert('userId must be a string', typeof userId === 'string');
    assert(
      'customData must be an object',
      typeof customData === 'object' && customData !== null && !Array.isArray(customData)
    );
    assert('customData must not contain nested objects', !hasNestedObjects(customData));

    assert(
      'customData must not contain launch darkly built-in attributes',
      !hasBuiltInAttributes(customData)
    );

    let userContext = this.getUserCustomContext();
    let newUser = {
      key: userId,
      custom: {
        ...userContext,
        ...customData,
      },
    };

    return await this._identify(newUser);
  }

  /**
   * Returns the `customData` object passed to identify().
   * Useful when you want to read the current user context without
   * gathering again all the data.
   *
   * @returns {Object|undefined} the `customData` object passed to identify()
   */
  getUserCustomContext() {
    // oxlint-disable-next-line typescript/no-unsafe-return
    return getCurrentContext()?.user?.custom;
  }

  get _initialCustomData() {
    const { hostname } = window.location;
    const custom = { hostname };
    return { custom, privateAttributeNames: Object.keys(custom) };
  }

  /**
   * Returns all feature flags for the current user.
   *
   * @returns {Object} all feature flags
   */
  get allFlags(): Record<string, boolean | string> {
    try {
      return getCurrentContext().allFlags;
    } catch (error) {
      // All component tests involving a react bifrost component would fail if we don't catch this error
      // even if they doesn't use a launchdarkly flag
      // We warn so the developer can set up LD in their tests if they use a flag
      // otherwise we ignore the error and pass an empty object
      // oxlint-disable-next-line no-console
      console.warn('LaunchDarkly not initialized', error);
      return {};
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    launchdarkly: LaunchdarklyService;
  }
}
